import { isEmpty, isNullOrUndefined, isString, sum } from '@maplecroft-typescript/utils';
import jsPDF from 'jspdf';
import { domToPng } from 'modern-screenshot';
import { PDFColor } from './enums';
import {
  AddBodyImageOptions,
  AddBoxedTextOptions,
  AddCircledTextOptions,
  AddHorizontalRuleOptions,
  AddHTMLAsImageOptions,
  AddIconOptions,
  AddImageOptions,
  AddListItemOptions,
  AddListOptions,
  AddTableOptions,
  AddTextOptions,
  DrawResult,
  FocusBox,
  FontWeight,
  PdfOptions,
  TableData,
  TextChunk,
  TextLine,
} from './models';
import { AddTextDrawResult } from './models/add-text-draw-result.model';
import { EmailLogo, LinkedInLogo, VeriskMaplecroftGlobeLogo, YoutubeLogo } from './pdf-icons';
import { UNSUPPORTED_SUBSCRIPT_CHARACTERS } from './unsupported-subscript-characters';
import { UNSUPPORTED_SUPERSCRIPT_CHARACTERS } from './unsupported-superscript-characters';
import { adjustColumnWidths, mmToPx, ptToMm, pxToMM, roundDownToNearestMultiple, toSubscript, toSuperscript } from './utils';

export class PDF {
  private readonly jspdf: jsPDF;
  private readonly footerHeight: number;
  // The default x coordinate for rendering aligned to the right of the page
  private readonly defaultContentX2: number;

  public readonly options: PdfOptions;

  // The page content width in mm
  public readonly pageContentWidth: number;

  // The page content height in mm
  public readonly pageContentHeight: number;

  // The current y position to begin starting rendering at
  public startY: number;

  private readonly logoWidth = 33.625;
  private readonly logoHeight = 10.125;

  private currentPageNumber = 1;

  constructor(options: Partial<PdfOptions> = {}) {
    this.options = {
      pageWidth: 210,
      pageHeight: 297,
      pageMarginX: 20,
      pageMarginY: 10,
      defaultFontSize: 12,
      defaultFontWeight: 'normal',
      defaultFontStyle: 'normal',
      defaultFontColor: PDFColor.Black,
      defaultTextAlign: 'left',
      defaultLineHeight: 1.2,
      defaultSpacing: 5,
      disclaimerIconSize: 8,
      ...options,
    };

    this.footerHeight = this.options.omitFooters ? 0 : this.options.pageMarginY + this.options.defaultSpacing;
    this.defaultContentX2 = this.options.pageWidth - this.options.pageMarginX;
    this.pageContentWidth = this.options.pageWidth - this.options.pageMarginX * 2;
    this.pageContentHeight = this.options.pageHeight - this.options.pageMarginY - this.footerHeight;

    this.startY = this.options.pageMarginY;

    this.jspdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      hotfixes: ['px_scaling'],
    });

    this.addHeader();
    this.addFooter();
  }

  /**
   * Saves the pdf with the provided filename.
   */
  public save(filename: string): void {
    this.jspdf.save(filename);
  }

  /**
   * Adds a horizontal line accross the current page
   */
  public addHorizontalRule(options: Partial<AddHorizontalRuleOptions> = {}): DrawResult {
    // Combine provided options with default options
    const {
      color = PDFColor.Primary,
      thickness = 0.25,
      x = this.options.pageMarginX,
      y = this.startY,
      width = this.options.pageWidth - this.options.pageMarginX,
      spacingTop = this.options.defaultSpacing,
      spacingBottom = 0,
      preventRender = false,
      preventStartYUpdate = false,
    } = options;

    const spacedY = y + spacingTop;

    if (!preventRender) {
      this.jspdf.setDrawColor(color);
      this.jspdf.setLineWidth(thickness);

      this.jspdf.line(x, spacedY, width, spacedY);
    }

    const drawnHeight = thickness + spacingTop + spacingBottom;

    if (!preventStartYUpdate && !preventRender) {
      this.startY = y + drawnHeight;
    }

    return { height: drawnHeight };
  }

  /**
   * Adds text to the current page. It will split the text over multiple pages if it will not fit unless configured otherwise.
   */
  public addText(text: string, options: Partial<AddTextOptions> = {}): AddTextDrawResult {
    const combinedOptions = this.combineTextOptions(options);
    const {
      url,
      align,
      spacingTop,
      spacingBottom,
      preventStartYUpdate,
      size,
      preventRender,
      preventSpanningMultiplePages,
      preventSpanningMultiplePagesAdditionalOffset,
    } = combinedOptions;

    // Replace escaped charaters with their unicode equivalents
    text = text.replace(/\t/g, ' ').replace(/\s/g, ' ');

    // The line height of the text to be rendered in mm
    const lineHeight = this.getLineHeight(combinedOptions);
    const sizeMM = ptToMm(size);
    const lineHeightOffset = (lineHeight - sizeMM) / 2;
    const lines = this.getTextLines(text, combinedOptions);
    const numberOfLines = lines.length;

    // The x position of the end of the last line
    let x2 = 0;
    // The y position of the last line
    let lastLineY = 0;

    const potentialTextHeight = numberOfLines * lineHeight + spacingTop + spacingBottom + preventSpanningMultiplePagesAdditionalOffset;
    const forceNewPageOnFirstLine = preventSpanningMultiplePages && potentialTextHeight + this.startY > this.pageContentHeight;

    lines.forEach(({ text, x, y }, lineIndex) => {
      y += spacingTop;

      const nextY = y + lineHeight;

      // If the text will not fit on the current page, add a new page
      if (!preventStartYUpdate && !preventRender && (nextY > this.pageContentHeight || (forceNewPageOnFirstLine && lineIndex === 0))) {
        this.addPage();

        const diff = y - this.startY;

        // Update the y position of the remaining lines
        lines.slice(lineIndex).forEach((line) => {
          line.y -= diff;
        });

        y = this.startY;

        // We have to set text style again because when we add a new page the text style is changed by the addHeader method.
        this.setTextStyle(combinedOptions);
      }

      if (!preventRender) {
        const initalTextColor = this.jspdf.getTextColor();

        if (url) {
          // Add a link to the text
          this.jspdf.link(x, y, this.jspdf.getTextWidth(text), ptToMm(size), { url });

          this.jspdf.setTextColor(PDFColor.Primary);
        }

        this.jspdf.text(text, x, y + lineHeightOffset, { align, baseline: 'top' });

        this.jspdf.setTextColor(initalTextColor);
      }

      const finalLine = lineIndex === numberOfLines - 1;

      if (!preventStartYUpdate && !preventRender) {
        let nextStartY = y + lineHeight;

        if (finalLine) {
          nextStartY += spacingBottom;
        }

        this.startY = nextStartY;
      }

      if (finalLine) {
        lastLineY = y;
        x2 = this.jspdf.getTextWidth(text) + x;
      }
    });

    return { lastLineY, x2, height: numberOfLines * lineHeight + spacingTop + spacingBottom };
  }

  /**
   * Adds a list to the current page. It will split list items over multiple pages if it will not fit on the current page.
   */
  public addList(items: string[], options: Partial<AddListOptions> = {}): DrawResult {
    const combinedOptions = this.combineListOptions(options);
    const { horizontalRule, itemSpacing, x, color, spacingTop } = combinedOptions;

    let totalHeight = 0;

    if (horizontalRule) {
      const horizontalRuleResult = this.addHorizontalRule({ x, color, spacingTop });

      totalHeight += horizontalRuleResult.height;
    }

    items.forEach((item, index) => {
      let spacingTop: number;
      let spacingBottom = 0;

      if (horizontalRule) {
        spacingTop = itemSpacing;
        spacingBottom = itemSpacing;
      } else if (index === 0) {
        spacingTop = combinedOptions.spacingTop;
      } else {
        spacingTop = itemSpacing;
      }

      const { height } = this.addListItem(item, {
        ...combinedOptions,
        spacingTop,
        spacingBottom,
        // Necessary to auto layout, otherwise it will render every item on the same line
        y: undefined,
      });

      totalHeight += height;
    });

    return { height: totalHeight };
  }

  /**
   * Combines the provided options with the default list options.
   */
  private combineListOptions(options: Partial<AddListOptions> = {}): AddListOptions {
    const { itemSpacing = 2, noBullets = false, horizontalRule = false } = options;

    return {
      ...this.combineTextOptions(options),
      itemSpacing,
      noBullets,
      horizontalRule,
    };
  }

  /**
   * Adds a list item. If the list item will not fit on the current page, a new page will be added and the list item will be rendered on the
   * new page.
   */
  private addListItem(text: string, options: Partial<AddListItemOptions> = {}): AddTextDrawResult {
    const combinedOptions = this.combineListItemOptions(options);
    const { x, color, spacingTop, number, noBullets: noBullet, horizontalRule, preventRender } = combinedOptions;
    let { y } = combinedOptions;

    let textX = x;

    if (!noBullet) {
      textX += this.options.defaultSpacing;
    }

    const renderHTMLOptions = { ...combinedOptions, x: textX };

    const horizontalRuleOptions: Partial<AddHorizontalRuleOptions> = {
      x,
      color,
      spacingTop: 0,
      spacingBottom: 0,
    };

    const listItemHeight =
      this.renderHTMLBlock(text, { ...renderHTMLOptions, preventRender: true }).height +
      this.addHorizontalRule({ ...horizontalRuleOptions, preventRender: true }).height;

    if (!preventRender && this.startY + listItemHeight > this.pageContentHeight) {
      this.addPage();

      y = this.startY;
      renderHTMLOptions.y = y;
    }

    if (!noBullet && !preventRender) {
      if (isNullOrUndefined(number)) {
        this.jspdf.setFillColor(color);

        const lineHeight = this.getLineHeight(combinedOptions);

        this.jspdf.circle(x + 2, y + spacingTop + lineHeight / 2, 0.5, 'F');
      } else {
        this.addText(`${number}.`, { ...combinedOptions, x, y, preventStartYUpdate: true });
      }
    }

    const htmlBlockResult = this.renderHTMLBlock(text, renderHTMLOptions);

    let height = htmlBlockResult.height;

    if (horizontalRule) {
      const ruleResult = this.addHorizontalRule(horizontalRuleOptions);

      height += ruleResult.height;
    }

    return { ...htmlBlockResult, height };
  }

  /**
   * Adds rich text HTML to the page. Primarily used for adding content from Nessie.
   */
  public addRichTextHTML(richText: string, options: Partial<AddTextOptions> = {}): DrawResult {
    const combinedOptions = this.combineTextOptions(options);
    const html = this.textToHTML(richText);

    let totalHeight = 0;

    Array.from(html.children).forEach((child, index) => {
      (combinedOptions as Partial<AddTextOptions>).y = index === 0 ? combinedOptions.y : undefined;

      switch (child.tagName) {
        case 'P': {
          const { height } = this.renderHTMLBlock(child as HTMLParagraphElement, combinedOptions);

          totalHeight += height;
          break;
        }
        case 'UL':
        case 'OL': {
          const { height } = this.renderHTMLList(child as HTMLUListElement, combinedOptions);

          totalHeight += height;
          break;
        }
      }
    });

    return { height: totalHeight };
  }

  /**
   * Renders a list from within rich text html.
   */
  private renderHTMLList(list: HTMLUListElement, options: Partial<AddListOptions> = {}): DrawResult {
    const combinedOptions = this.combineListOptions(options);
    const ordered = list.tagName === 'OL';
    // Only select direct LI children so nested list items are not double-rendered
    const items = Array.from(list.children).filter((el) => el.tagName === 'LI');

    let totalHeight = 0;

    items.forEach((item, index) => {
      const nestedLists = Array.from(item.children).filter((el) => el.tagName === 'UL' || el.tagName === 'OL');

      // Clone the item and strip any nested lists so only the inline text content is passed to addListItem
      const itemClone = item.cloneNode(true) as HTMLElement;
      Array.from(itemClone.children)
        .filter((el) => el.tagName === 'UL' || el.tagName === 'OL')
        .forEach((el) => el.remove());

      const { height } = this.addListItem(itemClone.innerHTML, {
        ...combinedOptions,
        spacingTop: index === 0 ? combinedOptions.spacingTop : combinedOptions.itemSpacing,
        number: ordered ? index + 1 : undefined,
        // Necessary to auto layout, otherwise it will render every item on the same line
        y: undefined,
      });

      totalHeight += height;

      // Recursively render nested lists with increased indentation
      const indentOffset = this.options.defaultSpacing;

      nestedLists.forEach((nestedList) => {
        const { height: nestedHeight } = this.renderHTMLList(nestedList as HTMLUListElement, {
          ...combinedOptions,
          x: combinedOptions.x + indentOffset,
          width: combinedOptions.width - indentOffset,
          spacingTop: 0,
          y: undefined,
        });

        totalHeight += nestedHeight;
      });
    });

    return { height: totalHeight };
  }

  /**
   * Adds text contained within a coloured box.
   */
  public addBoxedText(text: string, options: Partial<AddBoxedTextOptions> = {}): DrawResult {
    const defaultOptions = {
      paddingTop: 3,
      paddingRight: 3,
      paddingBottom: 3,
      paddingLeft: 3,
      backgroundColor: PDFColor.PaleBlue,
      outlineWidth: 0,
      outlineColor: PDFColor.LightGrey,
    };

    const combinedOptions: AddBoxedTextOptions = { ...defaultOptions, ...options, ...this.combineTextOptions(options) };

    let spacingTop = combinedOptions.spacingTop;

    const {
      backgroundColor,
      outlineWidth,
      outlineColor,
      x,
      y,
      width,
      height,
      spacingBottom,
      paddingTop,
      paddingRight,
      paddingBottom,
      paddingLeft,
      preventRender,
      preventStartYUpdate,
    } = combinedOptions;

    const textOptions: AddTextOptions = {
      ...combinedOptions,
      spacingTop: paddingTop,
      spacingBottom: paddingBottom,
      x: x + paddingLeft,
      firstLineIndent: x + paddingLeft,
      y: y + spacingTop,
      width: width - paddingLeft - paddingRight,
      preventStartYUpdate: true,
    };

    const boxHeight = height ?? this.renderHTMLBlock(text, { ...textOptions, preventRender: true }).height;
    const resultHeight = boxHeight + spacingTop + spacingBottom;

    if (resultHeight + this.startY > this.pageContentHeight) {
      this.addPage();

      spacingTop = 0;
    }

    if (!preventRender) {
      if (backgroundColor) {
        this.jspdf.setFillColor(backgroundColor);
        this.jspdf.rect(x, y + spacingTop, width, boxHeight, 'F');
      }

      if (outlineWidth > 0) {
        this.jspdf.setDrawColor(outlineColor);
        this.jspdf.setLineWidth(outlineWidth);
        this.jspdf.rect(x, y + spacingTop, width, boxHeight, 'S');
      }

      this.renderHTMLBlock(text, textOptions);

      if (!preventStartYUpdate) {
        this.startY += resultHeight;
      }
    }

    return { height: resultHeight };
  }

  /**
   * Adds text within a coloured circle. Text must be 2 characters or less to fit within the circle.
   */
  public addCircledText(text: string, options: Partial<AddCircledTextOptions> = {}): DrawResult {
    if (text.length > 2) {
      throw new Error('Circled text must be 2 characters or less.');
    }

    const defaultOptions = {
      backgroundColor: PDFColor.Primary,
      color: PDFColor.White,
      weight: 'bold' as FontWeight,
      size: 14,
      lineHeight: 1,
    };

    const combinedOptions: AddCircledTextOptions = {
      ...defaultOptions,
      ...options,
      ...this.combineTextOptions({ ...defaultOptions, ...options }),
    };

    const { backgroundColor, x, spacingBottom, preventRender, preventStartYUpdate } = combinedOptions;
    let { y } = combinedOptions;

    let spacingTop = combinedOptions.spacingTop;

    const textHeightMm = this.getLineHeight(combinedOptions);
    const diameter = textHeightMm * 1.5;
    const radius = diameter / 2;
    let resultHeight = diameter + spacingTop + spacingBottom;

    if (resultHeight + this.startY > this.pageContentHeight) {
      this.addPage();

      spacingTop = 0;
      resultHeight = diameter + spacingBottom;

      if (isNullOrUndefined(options.y)) {
        y = this.startY;
      }
    }

    if (!preventRender) {
      y += spacingTop;

      this.jspdf.setFillColor(backgroundColor ?? PDFColor.Primary);
      this.jspdf.circle(x + radius, y + radius, radius, 'F');

      this.setTextStyle(combinedOptions);

      const textWidth = this.jspdf.getTextWidth(text);
      const textX = x + radius - textWidth / 2;

      // The offset to apply to the position y of the text when centring
      const textHeightOffset = textHeightMm * 0.3039;

      const textOptions: AddTextOptions = {
        ...combinedOptions,
        spacingTop: 0,
        preventStartYUpdate: true,
        firstLineIndent: textX,
        x: textX,
        y: y - textHeightOffset + radius - (textHeightMm - textHeightOffset) / 2,
        color: PDFColor.PaleBlue,
      };

      this.addText(text, textOptions);

      if (!preventStartYUpdate) {
        this.startY += resultHeight;
      }
    }

    return { height: resultHeight, width: diameter, spacingTop };
  }

  /**
   * Dynamically loads and adds an image to the current page with optional title, subtitle, and source text. If the image and its supporting
   * text will not fit on the current page, a new page will be added and the image and its supporting text will be rendered on the new page.
   */
  public async addBodyImage(url: string, options: Partial<AddBodyImageOptions> = {}): Promise<DrawResult> {
    // Combine provided options with default options
    const { title, subtitle, source, width = 0, height = 0, spacingBottom = 0, compression = 'FAST' } = options;

    let { spacingTop = this.options.defaultSpacing } = options;

    const titleBoxSpacing = 3;
    const titleBoxTextWidth = this.pageContentWidth - titleBoxSpacing * 2;

    let totalHeight = 0;

    const titleOptions: Partial<AddBoxedTextOptions> = {
      color: PDFColor.White,
      weight: 'bold',
      spacingTop,
      spacingBottom,
      x: this.options.pageMarginX,
      backgroundColor: PDFColor.Secondary,
    };

    if (!isEmpty(title)) {
      totalHeight += this.addBoxedText(title, { ...titleOptions, preventRender: true }).height;
    }

    const subtitleOptions: Partial<AddTextOptions> = {
      color: PDFColor.Secondary,
      weight: 'bold',
      spacingTop: titleBoxSpacing,
      x: this.options.pageMarginX + titleBoxSpacing,
      width: titleBoxTextWidth,
    };

    if (!isEmpty(subtitle)) {
      totalHeight += this.addText(subtitle, { ...subtitleOptions, preventRender: true }).height;
    }

    // Calculate the height of the image to be rendered
    const imageHeight = (height / width) * this.pageContentWidth;
    const imageHeightWithSpacing = imageHeight + this.options.defaultSpacing;

    totalHeight += imageHeightWithSpacing;

    const sourceSpacing = 2;
    const sourceOptions: Partial<AddTextOptions> = { color: PDFColor.Grey, spacingTop: sourceSpacing, size: 8, style: 'italic' };
    const sourceRuleOptions: Partial<AddHorizontalRuleOptions> = { spacingTop: 0, color: PDFColor.LightGrey };

    if (!isEmpty(source)) {
      totalHeight += this.addText(source, { ...sourceOptions, preventRender: true }).height;
      totalHeight += this.addHorizontalRule({ ...sourceRuleOptions, preventRender: true }).height;
    }

    // If the height of the title, subtitle, image, and source is greater than the remaining height on the current page, add a new page.
    if (totalHeight + this.startY > this.pageContentHeight) {
      this.addPage();

      spacingTop = 0;
    }

    // Render the title
    if (!isEmpty(title)) {
      this.addBoxedText(title, titleOptions);
    }

    // Render the subtitle
    if (!isEmpty(subtitle)) {
      this.addText(subtitle, subtitleOptions);
    }

    await this.addImage(url, {
      width: this.pageContentWidth,
      height: imageHeight,
      compression,
      preventStartYUpdate: true,
    });

    // Update the start y position with the image height and any spacing
    this.startY += imageHeightWithSpacing;

    // Render the source text
    if (!isEmpty(source)) {
      this.addHorizontalRule(sourceRuleOptions);
      this.addText(source, sourceOptions);
    }

    return { height: totalHeight + spacingTop + spacingBottom };
  }

  public async addImage(url: string, options: Partial<AddImageOptions> = {}): Promise<DrawResult> {
    const { width = this.pageContentWidth, compression = 'SLOW', spacingBottom = 0, preventStartYUpdate } = options;

    let { height } = options;

    let spacingTop = isNullOrUndefined(options.spacingTop) ? this.options.defaultSpacing : options.spacingTop;

    const image = new Image();

    // Load the image
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(`Failed to load image: ${url}`);

      image.src = url;
    });

    if (isNullOrUndefined(height)) {
      const ratio = image.height / image.width;

      height = width * ratio;
    }

    let totalHeight = height + spacingTop + spacingBottom;

    if (totalHeight + this.startY > this.pageContentHeight) {
      this.addPage();

      spacingTop = 0;
      totalHeight = height + spacingBottom;
    }

    const { x = this.options.pageMarginX, y = this.startY + spacingTop } = options;

    // Render the image
    this.jspdf.addImage(image, 'PNG', x, y, width, height, '', compression);

    if (!preventStartYUpdate) {
      this.startY += totalHeight;
    }

    return {
      height: totalHeight,
      width: width,
    };
  }

  /**
   * Adds a Nessie "Focus box" to the current page. If the focus box does not fit on the current page a new page will be added and the focus
   * box will be rendered there.
   */
  public addFocusBox({ title, body }: FocusBox): void {
    const focusBoxPadding = 3;
    const focusBoxBackgroundColor = PDFColor.PaleBlue;

    const preTitle = 'Focus box';
    const preTitleOptions: Partial<AddBoxedTextOptions> = {
      spacingTop: 0,
      paddingTop: focusBoxPadding,
      paddingRight: focusBoxPadding,
      paddingBottom: 0,
      paddingLeft: focusBoxPadding,
      weight: 'bold',
      backgroundColor: focusBoxBackgroundColor,
    };

    const preTitleHeight = this.addBoxedText(preTitle, { ...preTitleOptions, preventRender: true }).height;

    const titleOptions: Partial<AddBoxedTextOptions> = {
      spacingTop: 0,
      paddingTop: 2,
      paddingRight: focusBoxPadding,
      paddingBottom: 0,
      paddingLeft: focusBoxPadding,
      weight: 'bold',
      color: PDFColor.Secondary,
      backgroundColor: focusBoxBackgroundColor,
    };

    const titleHeight = this.addBoxedText(title, { ...titleOptions, preventRender: true }).height;

    const bodyOptions: Partial<AddTextOptions> = {
      width: this.pageContentWidth - focusBoxPadding * 2,
      x: this.options.pageMarginX + focusBoxPadding,
      spacingTop: focusBoxPadding,
      spacingBottom: focusBoxPadding,
    };

    const htmlHeight = this.addRichTextHTML(body, {
      ...bodyOptions,
      preventRender: true,
    }).height;

    const totalHeight = preTitleHeight + titleHeight + htmlHeight;

    let spacingTop = this.options.defaultSpacing;

    if (totalHeight + this.startY > this.pageContentHeight) {
      this.addPage();

      spacingTop = 0;
    }

    this.addBoxedText(preTitle, { ...preTitleOptions, spacingTop });
    this.addBoxedText(title, titleOptions);

    this.jspdf.setFillColor(focusBoxBackgroundColor);
    this.jspdf.rect(this.options.pageMarginX, this.startY, this.pageContentWidth, htmlHeight, 'F');

    this.addRichTextHTML(body, bodyOptions);
  }

  /**
   * Adds a table to the current page. It will attempt to render the whole table on one page, otherwise it will ensure at least 3 rows below
   * the header are rendered on one page with the remainder of rows rendered on subsequent pages. By default it will try to ensure columns
   * are not narrower than 20mm.
   *
   * Realistically this can only handle 3-4 columns of data.
   */
  public addTable(data: TableData[][], options: Partial<AddTableOptions> = {}): void {
    const { title, source, minRowsPerPage = 3, spacingBottom = 0, firstRowIsHeader = true } = options;
    let { spacingTop = this.options.defaultSpacing } = options;

    const rows = data.map((row) => row.map((cell) => cell?.toString() || ''));

    const { columnWidthPercentages: externalColumnWidthPercentages } = options;

    const columnMinWidth = 20;
    const columnMaxWidth = this.pageContentWidth / 2;
    const cellPadding = 2;
    const minFontSize = 7;

    let size = this.options.defaultFontSize;
    let columnWidths: number[] = [];

    // Retry with a decreasing font size if columns are too wide to fit at the current size.
    for (;;) {
      const columnMinWidths: number[] = [];
      let columnWidthPercentages = externalColumnWidthPercentages;

      if (!columnWidthPercentages) {
        const rawColumnWidths: number[] = [];

        rows.forEach((row, rowIndex) => {
          this.setTextStyle({ size, weight: this.getFontWeightForRow(rowIndex, firstRowIsHeader) });

          row.forEach((cell, cellIndex) => {
            const cellWidth = this.jspdf.getTextWidth(cell);

            if (isNullOrUndefined(rawColumnWidths[cellIndex])) {
              rawColumnWidths[cellIndex] = cellWidth;
            } else {
              rawColumnWidths[cellIndex] = Math.max(rawColumnWidths[cellIndex], cellWidth);
            }

            const widestWordWidth =
              Math.ceil(
                Math.max(
                  ...cell
                    .split(' ')
                    .map((word) => word.trim())
                    .map((word) => this.jspdf.getTextWidth(word)),
                ),
              ) +
              cellPadding * 2;

            columnMinWidths[cellIndex] = Math.min(
              Math.max(columnMinWidths[cellIndex] ?? 0, widestWordWidth, columnMinWidth),
              columnMaxWidth,
            );
          });
        });

        const totalWidth = sum(rawColumnWidths);
        columnWidthPercentages = rawColumnWidths.map((width) => (width / totalWidth) * 100);
      }

      try {
        columnWidths = adjustColumnWidths(
          columnWidthPercentages.map((percentage) => (percentage / 100) * this.pageContentWidth),
          this.pageContentWidth,
          columnMinWidths,
        );
        break;
      } catch {
        if (size <= minFontSize) {
          throw new Error('Cannot lay out table: columns are too wide to fit even at minimum font size');
        }
        size -= 1;
      }
    }

    const rowHeights = rows.map((row, rowIndex) => {
      let maxHeight = 0;

      row.forEach((cell, columnIndex) => {
        const { height } = this.addText(cell, {
          size,
          weight: this.getFontWeightForRow(rowIndex, firstRowIsHeader),
          width: columnWidths[columnIndex] - cellPadding * 2,
          preventRender: true,
        });
        maxHeight = Math.max(maxHeight, height);
      });

      return maxHeight;
    });

    const totalRowHeight = sum(rowHeights);

    let titleHeight = 0;

    const titleOptions: Partial<AddTextOptions> = {
      weight: 'bold',
      color: PDFColor.Secondary,
      spacingTop,
      spacingBottom: 2,
    };

    if (!isEmpty(title)) {
      titleHeight = this.addText(title, { ...titleOptions, preventRender: true }).height;
    }

    let sourceHeight = 0;

    const sourceOptions: Partial<AddTextOptions> = {
      style: 'italic',
      size: 8,
      color: PDFColor.Grey,
      spacingTop: 2,
    };

    if (!isEmpty(source)) {
      sourceHeight = this.addText(source, { ...sourceOptions, preventRender: true }).height;
    }

    const fullHeight = totalRowHeight + titleHeight + sourceHeight;

    const cantFitFullyOnCurrentPage = fullHeight + spacingTop + this.startY > this.pageContentHeight;
    const canFitFullOnNewPage = fullHeight <= this.pageContentHeight;
    const cantFitTitleAndMinRowsOnCurrentPage =
      this.startY + spacingTop + titleHeight + sum(rowHeights.slice(0, minRowsPerPage)) > this.pageContentHeight;

    if ((cantFitFullyOnCurrentPage && canFitFullOnNewPage) || cantFitTitleAndMinRowsOnCurrentPage) {
      this.addPage();

      spacingTop = 0;
    }

    if (!isEmpty(title)) {
      this.addText(title, { ...titleOptions, spacingTop });
      spacingTop = 0;
    }

    rows.forEach((row, rowIndex) => {
      if (rowIndex === 0 && isEmpty(title)) {
        this.startY += spacingTop;
      }

      const height = rowHeights[rowIndex];

      if (this.startY + height > this.pageContentHeight) {
        this.addPage();
      }

      row.forEach((cell, columnIndex) => {
        this.addBoxedText(cell, {
          x: this.options.pageMarginX + sum(columnWidths.slice(0, columnIndex)),
          height,
          size,
          weight: this.getFontWeightForRow(rowIndex, firstRowIsHeader),
          width: columnWidths[columnIndex],
          paddingTop: cellPadding,
          paddingRight: cellPadding,
          paddingBottom: cellPadding,
          paddingLeft: cellPadding,
          spacingTop: 0,
          spacingBottom: 0,
          outlineWidth: 0.25,
          backgroundColor: rowIndex === 0 && firstRowIsHeader ? PDFColor.PaleBlue : undefined,
          preventStartYUpdate: true,
        });
      });

      this.startY += height;
    });

    this.startY += spacingBottom;
  }

  /**
   * Converts a HTML element and its contents to an image and renders it to the page. If the image is too tall to fit on one page it will
   * split the image over multiple pages. You can specify the crop multiple to ensure that the image is split into chunks a multiple of that
   * height. E.g. if the crop multiple is 36, it could crop slices to 36, 72, 108, 144, etc.
   */
  public async addHTMLAsImage(element: HTMLElement, options: Partial<AddHTMLAsImageOptions> = {}): Promise<void> {
    // Combine provided options with default options
    const { spacingTop = this.options.defaultSpacing, spacingBottom = 0 } = options;
    const {
      width = this.pageContentWidth,
      // The width of the window in CSS pixels
      windowWidth = element.clientWidth,
      cropMultiplePx = 1,
      minImageHeightMM = 100,
      pixelRatio = 2,
      imageQuality = 0.92,
    } = options;

    /**
     * Convert the HTML into an image. This might be larger than what can fit on a page so below we will handle splitting the image over
     * multiple pages.
     */
    const fullImageData = await domToPng(element, { width: windowWidth, scale: pixelRatio });

    const fullImage = new Image();

    await new Promise<void>((resolve) => {
      fullImage.onload = () => resolve();
      fullImage.src = fullImageData;
    });

    let drawnPixels = 0;

    const fullImageToPageContentWidthRatio = fullImage.width / mmToPx(this.pageContentWidth);
    const resultImageTotalHeight = fullImage.height / fullImageToPageContentWidthRatio;
    const scaledCropMultiple = cropMultiplePx * (fullImage.width / windowWidth);

    while (drawnPixels < fullImage.height) {
      let remainingPageMM = this.pageContentHeight - this.startY;

      let addedPage = false;

      if (
        // Add a new page if there is less than the minimum image height remaining on the current page
        remainingPageMM < minImageHeightMM ||
        // Add a new page if the remaining height of the image is greater than the remaining height of the page
        (drawnPixels > 0 && mmToPx(remainingPageMM) < resultImageTotalHeight - drawnPixels / fullImageToPageContentWidthRatio)
      ) {
        this.addPage();

        addedPage = true;

        // Update to the availabe height of the new page
        remainingPageMM = this.pageContentHeight - this.startY;
      }

      // The height of the current crop, rounded to the nearest provided multiple
      const remainingHeightPx = Math.ceil(
        roundDownToNearestMultiple(
          Math.min(mmToPx(remainingPageMM * fullImageToPageContentWidthRatio), fullImage.height - drawnPixels),
          scaledCropMultiple,
        ),
      );

      const cropCanvas = new OffscreenCanvas(fullImage.width, remainingHeightPx);
      const cropContext = cropCanvas.getContext('2d');

      if (!cropContext) {
        throw new Error('Failed to get 2d context for cropped PDF image.');
      }

      // Fill canvas with white background to avoid black bg when converted to jpeg
      cropContext.fillStyle = 'white';
      cropContext.fillRect(0, 0, fullImage.width, remainingHeightPx);

      // Create a crop of the image
      cropContext.drawImage(fullImage, 0, drawnPixels, fullImage.width, remainingHeightPx, 0, 0, fullImage.width, remainingHeightPx);

      const imageHeightMM = pxToMM(remainingHeightPx / fullImageToPageContentWidthRatio);

      const blob = await cropCanvas.convertToBlob({ type: 'image/jpeg', quality: imageQuality });
      const imageData = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });

      if (imageData.startsWith('data:image/jpeg;base64')) {
        this.jspdf.addImage(
          imageData,
          'JPEG',
          this.options.pageMarginX,
          this.startY + (drawnPixels === 0 && !addedPage ? spacingTop : 0),
          width,
          imageHeightMM,
        );
      } else {
        throw new Error(`Error while downloading PDF. ImageData: ${imageData}`);
      }

      drawnPixels += remainingHeightPx + spacingBottom;
      this.startY += imageHeightMM + spacingBottom;
    }
  }

  /**
   * Adds the standard disclaimer page to the end of the document.
   */
  public addDisclaimerPage(): void {
    this.addPage();

    this.addText('Disclaimer', { weight: 'bold', spacingTop: 0, size: 14, color: PDFColor.Secondary });

    this.addText('Strictly confidential', { weight: 'bold' });

    this.addText(
      `These materials, including any updates to them, are published by and remain subject to the copyright of the Maplecroft.Net Limited (“Verisk Maplecroft”), and are made available to clients of Verisk Maplecroft under terms agreed between Verisk Maplecroft and those clients. The use of these materials is governed by the terms and conditions of the agreement under which they were provided. The content and conclusions contained are confidential and may not be disclosed to any other person without Verisk Maplecroft’s prior written permission. Verisk Maplecroft makes no warranty or representation about the accuracy or completeness of the information and data contained in these materials, which are provided ‘as is’. The opinions expressed in these materials are those of Verisk Maplecroft, and nothing contained in them constitutes an offer to buy or to sell securities, or investment advice. Verisk Maplecroft’s products do not provide a comprehensive analysis of the financial position or prospects of any company or entity and nothing in any such product should be taken as comment regarding the value of the securities of any entity. Verisk Maplecroft does not warrant or represent that these materials are appropriate or sufficient for your purposes. If, notwithstanding the foregoing, you or any other person relies upon these materials in any way, Verisk Maplecroft does not accept, and hereby disclaims to the extent permitted by law, all liability for any loss and damage suffered arising in connection with such reliance. Please also note that the laws of certain jurisdictions may prohibit or regulate the dissemination of certain types of information contained in these materials, such as maps, and accordingly it is your responsibility to ensure that any dissemination of such information across national boundaries within your organisation is permitted under the laws of the relevant jurisdiction. Copyright © ${new Date().getFullYear()}, Maplecroft.Net Limited. All rights reserved. Verisk Maplecroft is a Verisk Analytics business.`,
      { size: 10 },
    );

    this.addHorizontalRule({ color: PDFColor.LightGrey });

    const contactResult = this.addText('Contact ', { preventStartYUpdate: true });

    const emailResult = this.addText('info@maplecroft.com', {
      url: 'mailto:info@maplecroft.com',
      firstLineIndent: contactResult.x2,
      preventStartYUpdate: true,
    });

    this.addText(' for more information about how our data and research can help you.', { firstLineIndent: emailResult.x2 });

    const iconSpacing = 2;

    const linkedInResult = this.addIcon(LinkedInLogo, 'https://www.linkedin.com/company/maplecroft/', {
      preventStartYUpdate: true,
    });

    const youtubeResult = this.addIcon(YoutubeLogo, 'https://www.youtube.com/channel/UCy2Acc6OO-H_koBaLcJQLfw/featured', {
      preventStartYUpdate: true,
      x: linkedInResult.x2 + iconSpacing,
    });

    const emailLogoResult = this.addIcon(EmailLogo, 'mailto:info@maplecroft.com', {
      preventStartYUpdate: true,
      x: youtubeResult.x2 + iconSpacing,
    });

    this.addText('maplecroft.com', {
      x: emailLogoResult.x2 + this.options.defaultSpacing,
      preventStartYUpdate: true,
      weight: 'bold',
      spacingTop: this.options.defaultSpacing + this.options.disclaimerIconSize / 4,
      url: 'https://maplecroft.com',
      color: PDFColor.Black,
    });

    const logoX = this.options.pageWidth - this.options.pageMarginX - this.logoWidth - 3;
    this.addLogo({ x: logoX, y: this.startY, url: 'https://verisk.com' });

    this.addText('Verisk Maplecroft is a Verisk business.', {
      size: 6,
      align: 'right',
      x: this.options.pageWidth - this.options.pageMarginX,
      y: this.startY + this.logoHeight,
      spacingTop: 2,
    });
  }

  /**
   * Adds the Verisk Maplecroft globe logo in the provided position.
   */
  private addLogo({ x, y, url }: { x: number; y: number; url?: string }): void {
    this.jspdf.addImage(VeriskMaplecroftGlobeLogo, 'PNG', x, y, this.logoWidth, this.logoHeight);

    if (url) {
      this.jspdf.link(x, y, this.logoWidth, this.logoHeight, { url });
    }
  }

  /**
   * For adding social icons on the disclaimer page.
   */
  private addIcon(icon: string, url: string, options: Partial<AddIconOptions> = {}): { x2: number } {
    const {
      width = this.options.disclaimerIconSize,
      height = this.options.disclaimerIconSize,
      x = this.options.pageMarginX,
      y = this.startY,
      spacingTop = this.options.defaultSpacing,
      spacingBottom = 0,
      preventStartYUpdate = false,
      preventRender = false,
    } = options;

    if (!preventRender) {
      this.jspdf.addImage(icon, 'PNG', x, y + spacingTop, width, height);
      this.jspdf.link(x, y + spacingTop, width, height, { url });
    }

    if (!preventStartYUpdate && !preventRender) {
      this.startY += height + spacingTop + spacingBottom;
    }

    return { x2: x + width };
  }

  /**
   * Gets the weight of the font for a table row based off its index.
   */
  private getFontWeightForRow(rowIndex: number, firstRowIsHeader: boolean): FontWeight {
    if (rowIndex === 0 && firstRowIsHeader) {
      return 'bold';
    }

    return 'normal';
  }

  /**
   * Combines list item options with default options.
   */
  private combineListItemOptions(options: Partial<AddListItemOptions> = {}): AddListItemOptions {
    return {
      ...options,
      ...this.combineListOptions(options),
    };
  }

  /**
   * Renders a block of rich text HTML content. This will convert superscript and subscript HTML to the equivalent unicode characters where
   * possible, and format text as bold, italic, or a link where required.
   */
  private renderHTMLBlock(value: HTMLElement | string, options: Partial<AddTextOptions> = {}): AddTextDrawResult {
    const html = isString(value) ? this.textToHTML(value) : value;
    let htmlString = html.innerHTML
      // Decode HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'");

    const subSupItems = Array.from(html.querySelectorAll('sub, sup'));

    subSupItems.forEach(({ tagName, textContent, outerHTML }) => {
      let replacement: string;

      if (tagName === 'SUB') {
        replacement = toSubscript(textContent, UNSUPPORTED_SUBSCRIPT_CHARACTERS);
      } else {
        replacement = toSuperscript(textContent, UNSUPPORTED_SUPERSCRIPT_CHARACTERS);
      }

      htmlString = htmlString.replace(outerHTML, replacement);
    });

    // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
    html.innerHTML = htmlString;

    const itemsToFormat = Array.from(html.querySelectorAll('a, b, strong, i')).filter(({ parentNode }) => parentNode === html);
    const totalItemsToFormat = itemsToFormat.length;

    const textChunks = new Array<TextChunk>();

    const { style, y } = options;
    let { weight } = options;

    if (totalItemsToFormat > 0) {
      itemsToFormat.forEach((item, index) => {
        const { outerHTML, tagName, innerHTML, textContent } = item;

        const [before, after] = htmlString.split(outerHTML);

        if (!isNullOrUndefined(before) && before.length) {
          textChunks.push({ text: before, weight, style });
        }

        htmlString = after;

        switch (tagName) {
          case 'A': {
            const html = document.createElement('div');

            // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
            html.innerHTML = innerHTML;

            if (html.querySelector('b, strong')) {
              weight = 'bold';
            }

            textChunks.push({ text: textContent, url: (item as HTMLAnchorElement).href, weight, style });
            break;
          }
          case 'B':
          case 'STRONG': {
            textChunks.push({ text: textContent, weight: 'bold', style });
            break;
          }
          case 'I': {
            textChunks.push({ text: textContent, weight, style: 'italic' });
            break;
          }
        }

        if (index === totalItemsToFormat - 1 && !isEmpty(after)) {
          textChunks.push({ text: after, weight, style });
        }
      });
    } else {
      textChunks.push({ text: htmlString, weight, style });
    }

    let previousX: number = options.x ?? this.options.pageMarginX;
    let previousY: number = y ?? this.startY;
    let totalHeight = 0;

    textChunks.forEach(({ text, url, weight, style }, index) => {
      const { x2, lastLineY, height } = this.addText(text, {
        ...options,
        url,
        firstLineIndent: previousX,
        y: previousY,
        weight,
        style,
        spacingTop: index > 0 ? 0 : options.spacingTop,
      });

      if (previousY !== lastLineY) {
        totalHeight += height;
      }

      previousX = x2;
      previousY = lastLineY;
    });

    return {
      x2: previousX,
      lastLineY: previousY,
      height: totalHeight,
    };
  }

  /**
   * Adds a header to the current page with the configured titles.
   */
  private addHeader(): void {
    // How far to offset the logo on the Y axis to centre it between the 3 title lines.
    let logoYOffset = 0;
    let horizontalRuleYOffset = 0;

    if (this.options.headers) {
      const { top, middle, bottom } = this.options.headers;

      const headingOptions: Partial<AddTextOptions> = {
        align: 'right',
        x: this.defaultContentX2,
        spacingTop: 0,
        size: 15,
      };

      if (top && middle && bottom) {
        logoYOffset = ptToMm(headingOptions.size ?? this.options.defaultFontSize);
      }

      if (top) {
        this.addText(top, { ...headingOptions, color: PDFColor.Primary, weight: 'bold' });
      }

      if (middle) {
        this.addText(middle, { ...headingOptions, color: PDFColor.Primary });
      }

      if (bottom) {
        this.addText(bottom, { ...headingOptions, color: PDFColor.Secondary, weight: 'bold' });
      }
    } else {
      horizontalRuleYOffset = this.logoHeight;
    }

    this.addHorizontalRule({ y: this.startY + horizontalRuleYOffset, spacingBottom: this.options.defaultSpacing });

    this.addLogo({ x: this.options.pageMarginX, y: this.options.pageMarginY + logoYOffset });
  }

  /**
   * Adds a footer to the current page.
   */
  private addFooter(): void {
    if (!this.options.omitFooters) {
      const fontSize = 8;

      const footerY = this.options.pageHeight - this.options.pageMarginY - ptToMm(fontSize);
      const footerTextOptions: Partial<AddTextOptions> = {
        size: fontSize,
        color: PDFColor.Grey,
        y: footerY,
        spacingTop: 0,
        preventStartYUpdate: true,
      };

      const copyrightText = `© Verisk Maplecroft ${new Date().getFullYear()} | `;

      this.addText(copyrightText, footerTextOptions);

      this.addText('maplecroft.com', {
        ...footerTextOptions,
        x: this.options.pageMarginX + this.jspdf.getTextWidth(copyrightText),
        url: 'https://maplecroft.com',
      });

      if (this.options.pageNumbers) {
        this.addText(this.currentPageNumber.toString(), {
          ...footerTextOptions,
          x: this.options.pageWidth - this.options.pageMarginX - this.jspdf.getTextWidth(this.currentPageNumber.toString()),
        });
      }

      this.addHorizontalRule({
        y: footerY - this.getLineHeight(footerTextOptions),
        spacingTop: 0,
        spacingBottom: 0,
        preventStartYUpdate: true,
      });
    }
  }

  /**
   * Combines the provided text options with the default text options to return a full set of text options.
   */
  private combineTextOptions(options: Partial<AddTextOptions>): AddTextOptions {
    const {
      align = this.options.defaultTextAlign,
      size = this.options.defaultFontSize,
      style = this.options.defaultFontStyle,
      weight = this.options.defaultFontWeight,
      url,
      color = url ? PDFColor.Primary : this.options.defaultFontColor,
      lineHeight = this.options.defaultLineHeight,
      spacingTop = this.options.defaultSpacing,
      spacingBottom = 0,
      preventRender = false,
      x = this.options.pageMarginX,
      y = this.startY,
      width = align === 'right' ? this.pageContentWidth : this.options.pageWidth - x - this.options.pageMarginX,
      preventSpanningMultiplePages = false,
      preventSpanningMultiplePagesAdditionalOffset = 0,
    } = options;
    const { firstLineIndent = x, preventStartYUpdate = preventRender } = options;

    return {
      align,
      size,
      style,
      weight,
      url,
      color,
      lineHeight,
      spacingTop,
      spacingBottom,
      preventStartYUpdate,
      x,
      y,
      width,
      firstLineIndent,
      preventRender,
      preventSpanningMultiplePages,
      preventSpanningMultiplePagesAdditionalOffset,
    };
  }

  /**
   * Sets the text style for the provided text options.
   */
  private setTextStyle(options: Partial<AddTextOptions>): void {
    const { weight, style, size, color, lineHeight } = this.combineTextOptions(options);

    this.jspdf.setFont('Roboto', `${weight}-${style}`);
    this.jspdf.setFontSize(size);
    this.jspdf.setTextColor(color);
    this.jspdf.setLineHeightFactor(lineHeight);
  }

  /**
   * Gets the line height of the current text style in mm.
   */
  private getLineHeight(options: Partial<AddTextOptions> = {}): number {
    this.setTextStyle(options);

    return ptToMm(this.jspdf.getLineHeight());
  }

  /**
   * Gets the text lines to render and their x and y positions.
   */
  private getTextLines(text: string, options: Partial<AddTextOptions>, forceSplit?: boolean): TextLine[] {
    const { align, firstLineIndent, x, y, width } = this.combineTextOptions(options);

    if (isEmpty(text)) {
      return [
        {
          text,
          x: firstLineIndent,
          y,
        },
      ];
    }

    const firstLineWidth = align === 'right' ? this.pageContentWidth : width + (x - firstLineIndent);
    let [firstLine] = this.splitTextToLines(text, firstLineWidth, options, forceSplit);

    const words = text.split(' ');

    // Ensure the first line only contains full words
    firstLine = firstLine
      .split(' ')
      .filter((firstLineWord, index) => {
        const word = words[index];

        return firstLineWord === word;
      })
      .join(' ');

    const lineHeight = this.getLineHeight(options);

    // If no text will fit on the x axis, begin on the line below from the left
    if (firstLine.length < 1 && x !== firstLineIndent) {
      return this.getTextLines(text, { ...options, y: y + lineHeight, firstLineIndent: x }, true);
    }

    const result: TextLine[] = [];
    let nextLineYOffset = 0;

    // Only add the first line if it is not empty
    if (firstLine.length > 0) {
      result.push({
        text: firstLine,
        x: firstLineIndent,
        y,
      });

      nextLineYOffset = 1;
    }

    const textWithFirstLineRemoved = text.replace(firstLine, '');
    const isJustUrl = !textWithFirstLineRemoved.includes(' ') && textWithFirstLineRemoved.startsWith('http');

    return [
      ...result,
      ...this.splitTextToLines(textWithFirstLineRemoved, width, options, isJustUrl)
        .filter((text) => !isEmpty(text))
        .map((text, index) => ({
          text: text.replace(/^ /, ''),
          x,
          y: y + lineHeight * (index + nextLineYOffset),
        })),
    ];
  }

  /**
   * Splits text into lines that will fit within the specified width.
   */
  private splitTextToLines(text: string, width: number, options: Partial<AddTextOptions> = {}, forceSplit = false): string[] {
    this.setTextStyle(options);

    const wordCount = text.split(' ').length;

    const result = this.jspdf.splitTextToSize(text, width) as string[];

    // Prevent single words being split over multiple lines
    if (!forceSplit && wordCount < result.length && wordCount === 1) {
      return ['', text];
    }

    return result;
  }

  /**
   * Adds a new page to the PDF with the configured headers and footers. Content will be added to the new page.
   */
  public addPage(): void {
    this.currentPageNumber += 1;

    this.jspdf.addPage();
    // Reset the y position to the top of the new page content area
    this.startY = this.options.pageMarginY;

    this.addHeader();
    this.addFooter();
  }

  /**
   * Converts a string of text or HTML elements as a string into actual HTML elements.
   */
  private textToHTML(text: string): HTMLDivElement {
    const html = document.createElement('div');

    // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
    html.innerHTML = text;

    return html;
  }
}
